/**
 * <infinite-carousel>
 * ---------------------------------------------------------------------------
 * A seamless, looping variant of Prestige's native <scroll-carousel>.
 *
 * It subclasses scroll-carousel so it keeps ALL of the stock behaviour (native
 * scroll, scroll-snap, drag-to-scroll, the <carousel-prev/next-button> wiring
 * via aria-controls, resize handling, image preloading). On top of that it:
 *
 *   1. Clones the full set of cells once before the real set and once after it,
 *      so the DOM becomes  [head clones][real cells][tail clones].
 *   2. Parks the scroll position inside the middle (real) block.
 *   3. Overrides next()/previous() to scroll by one "page" (the number of
 *      cells currently visible) instead of clamping at the ends.
 *   4. On every scroll settle, if we've drifted into the head/tail clones it
 *      instantly shifts the scroll position by exactly one block width. Because
 *      the clones are pixel-identical to the real cells, that shift is
 *      invisible — the carousel appears to loop forever.
 *
 * The block width is always a whole number of cell "pitches", so shifting by it
 * (and paging by whole cells) keeps every resting position on a valid snap
 * point — no snap jitter.
 */
(() => {
  const TAG = 'infinite-carousel';

  customElements.whenDefined('scroll-carousel').then(() => {
    if (customElements.get(TAG)) return;

    const ScrollCarousel = customElements.get('scroll-carousel');

    class InfiniteCarousel extends ScrollCarousel {
      #ready = false;
      #realCount = 0;

      connectedCallback() {
        super.connectedCallback();
        // Wait one frame so cells have laid out and offsets are measurable.
        requestAnimationFrame(() => this.#init());
      }

      #init() {
        if (this.#ready) return;

        const reals = Array.from(this.children);
        this.#realCount = reals.length;

        // Nothing to loop if there aren't enough cells to overflow the viewport.
        if (reals.length < 2 || this.scrollWidth - this.clientWidth < 2) return;

        const makeClone = (cell) => {
          const clone = cell.cloneNode(true);
          clone.setAttribute('data-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('id');
          // Strip theme-editor hooks so clones don't register as real blocks.
          Array.from(clone.attributes).forEach((attr) => {
            if (attr.name.startsWith('data-shopify')) clone.removeAttribute(attr.name);
          });
          // Keep cloned interactive elements out of the tab order.
          clone.querySelectorAll('a[href], button, input, select, textarea, [tabindex]').forEach((el) => {
            el.setAttribute('tabindex', '-1');
          });
          return clone;
        };

        const tail = document.createDocumentFragment();
        const head = document.createDocumentFragment();
        reals.forEach((cell) => tail.appendChild(makeClone(cell)));
        reals.forEach((cell) => head.appendChild(makeClone(cell)));

        this.appendChild(tail);
        this.insertBefore(head, this.firstChild);

        this.#ready = true;

        this.#parkAtStart();
        this.#enableArrows();

        this.addEventListener('scrollend', this.#normalize, { passive: true });
        new ResizeObserver(() => {
          if (this.#ready) this.#normalize();
        }).observe(this);
      }

      /** Width of a single [head|real|tail] block — an exact multiple of the cell pitch. */
      get #blockWidth() {
        const cells = this.children;
        return cells[this.#realCount].offsetLeft - cells[0].offsetLeft;
      }

      /** Distance from one cell's start to the next (cell width + gap). */
      get #cellPitch() {
        const cells = this.children;
        if (cells.length < 2) return this.clientWidth || 1;
        return Math.abs(cells[1].offsetLeft - cells[0].offsetLeft) || cells[0].clientWidth || 1;
      }

      /** How far one arrow click moves: the number of fully visible cells. */
      get #page() {
        const pitch = this.#cellPitch;
        const visible = Math.max(1, Math.round(this.clientWidth / pitch));
        return visible * pitch;
      }

      /** Position the first real cell flush against the left edge of the viewport. */
      #parkAtStart() {
        const cell = this.children[this.#realCount];
        if (!cell) return;
        const pad = parseFloat(getComputedStyle(this).scrollPaddingLeft) || 0;
        this.scrollLeft = Math.round(cell.offsetLeft - pad);
      }

      /** Shift back into the real block if we've scrolled into the clones. */
      #normalize = () => {
        if (!this.#ready) return;
        const w = this.#blockWidth;
        if (this.scrollLeft < w - 1) {
          this.scrollLeft += w;
        } else if (this.scrollLeft >= 2 * w - 1) {
          this.scrollLeft -= w;
        }
      };

      /** We always sit mid-track, so the arrows should never be disabled. */
      #enableArrows() {
        if (!this.id) return;
        document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((wrapper) => {
          const button = wrapper.firstElementChild;
          if (button) button.disabled = false;
        });
      }

      next() {
        if (!this.#ready) return super.next();
        this.#normalize();
        this.scrollBy({ left: this.#page, behavior: 'smooth' });
      }

      previous() {
        if (!this.#ready) return super.previous();
        this.#normalize();
        this.scrollBy({ left: -this.#page, behavior: 'smooth' });
      }
    }

    customElements.define(TAG, InfiniteCarousel);
  });
})();
